/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Fragment, useContext } from 'react';

import { MOMENT_FORMATS, formatDateTimeDisplay, momentWithUserTimezone } from 'js/utils/DateTimeUtils';

import { Divider, Grid } from '@coursera/cds-core';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { PrivateSessionDrawerContext } from 'bundles/enterprise-admin-drawers/components/PrivateSessionEditDrawerContent';
import AcademicIntegrityFeaturesStep from 'bundles/enterprise-admin-drawers/components/components/AcademicIntegrityFeaturesStep';
import AutoEnrollStep from 'bundles/enterprise-admin-drawers/components/components/AutoEnrollStep';
import AvailabilityStep from 'bundles/enterprise-admin-drawers/components/components/AvailabilityStep';
import OfferCourseForCreditStep from 'bundles/enterprise-admin-drawers/components/components/OfferCourseForCreditStep';
import type { CourseSession, CustomSessionOptionType } from 'bundles/enterprise-admin-drawers/components/types';
import type { DateRangeValue } from 'bundles/enterprise-ui/components/DateRangeInput';
import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';
import type {
  CreateSessionEligibility,
  EnterpriseAdminSessionSelectionOptionType,
} from 'bundles/naptimejs/resources/__generated__/EnterpriseProductConfigurationsV1';
import type OnDemandCourseSessions from 'bundles/naptimejs/resources/onDemandCourseSessions.v1';

type Step1Props = {
  selectableSessions: Array<EnterpriseAdminSessionSelectionOptionType | CustomSessionOptionType>;
  sessions: Array<OnDemandCourseSessions>;
  createSessionEligibility: CreateSessionEligibility;
  selectedSessionOption?: EnterpriseAdminSessionSelectionOptionType | CustomSessionOptionType;
  selectedSession?: CourseSession;
  dateError?: string | React.ReactNode;
  isSelectedForCredit: boolean;
  isSelectedToRequireAllItemCompletion: boolean;
  isAutoEnrolling: boolean;
  creationMode: boolean;
  hasUpdatedAvailability: boolean;
  hasUpdatedCreditStatus: boolean;
  hasUpdatedItemCompletionStatus: boolean;
  hasUpdatedAutoEnroll: boolean;
  onChangeSessionOption: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeDate: (date: DateRangeValue) => void;
  onChangeName: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeCourseCredit: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeItemCompletion: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeAutoEnroll: (e: React.ChangeEvent<HTMLInputElement>) => void;
  productId?: string;
  setDateError: (dateError: string | React.ReactNode | undefined) => void;
  useCampusWording?: boolean;
};

export const sectionWrapperStyle = css`
  display: flex;
  gap: 12px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 36px;
  margin-bottom: 16px;
`;

export const sectionSubtitleStyle = css`
  color: #525252;
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 6px;
  text-transform: uppercase;
`;

export const sectionEditButtonStyle = css`
  font-weight: normal;
  border-radius: 0;
  padding: 6px 24px;
`;

const PrivateSessionEditDrawerStep1: React.FC<Step1Props> = ({
  selectableSessions,
  sessions,
  createSessionEligibility,
  selectedSessionOption,
  selectedSession,
  dateError,
  isSelectedForCredit,
  isSelectedToRequireAllItemCompletion,
  isAutoEnrolling,
  creationMode,
  hasUpdatedAvailability,
  hasUpdatedCreditStatus,
  hasUpdatedItemCompletionStatus,
  hasUpdatedAutoEnroll,
  onChangeSessionOption,
  onChangeDate,
  onChangeName,
  onChangeCourseCredit,
  onChangeItemCompletion,
  onChangeAutoEnroll,
  productId,
  setDateError,
  useCampusWording,
}) => {
  const enableCourseProfiles = EnterpriseExperiments.get('enableCourseProfiles');
  const isAvailabilityEnded = Boolean(
    selectedSession?.endsAt && momentWithUserTimezone(selectedSession?.endsAt).isBefore(momentWithUserTimezone())
  );
  const { isSelectionDisabled } = useContext(PrivateSessionDrawerContext);

  return (
    <Grid container spacing={24}>
      <Grid item>
        <OfferCourseForCreditStep
          isSelectedForCredit={isSelectedForCredit}
          isSelectedToRequireAllItemCompletion={isSelectedToRequireAllItemCompletion}
          isOpen={
            creationMode || hasUpdatedCreditStatus || hasUpdatedItemCompletionStatus || Boolean(isSelectionDisabled)
          }
          onChangeCourseCredit={onChangeCourseCredit}
          onChangeItemCompletion={onChangeItemCompletion}
          productId={productId}
          useCampusWording={useCampusWording}
        />
      </Grid>
      <Divider color="light" css={{ width: '100%' }} />
      {enableCourseProfiles && (
        <Fragment>
          <Grid item css={{ width: '100%' }}>
            <AcademicIntegrityFeaturesStep
              onChange={() => {}}
              gradedItemLocking={false}
              restrictCourseraCoachAccess={false}
              restrictCourseContentAccess={false}
            />
          </Grid>
          <Divider color="light" css={{ width: '100%' }} />
        </Fragment>
      )}

      <Grid item css={{ width: '100%' }}>
        <AvailabilityStep
          selectableSessions={selectableSessions}
          sessions={sessions}
          createSessionEligibility={createSessionEligibility}
          selectedSessionOption={selectedSessionOption}
          selectedSession={selectedSession}
          dateError={dateError}
          isOpen={creationMode || hasUpdatedAvailability || Boolean(isSelectionDisabled)}
          onChangeSessionOption={onChangeSessionOption}
          onChangeDate={onChangeDate}
          onChangeName={onChangeName}
          productId={productId}
          setDateError={setDateError}
        />
      </Grid>
      <Divider color="light" css={{ width: '100%' }} />
      <React.Fragment>
        <Grid item css={{ width: '100%' }}>
          <AutoEnrollStep
            isAutoEnrolling={isAutoEnrolling}
            hasUpdatedAutoEnroll={hasUpdatedAutoEnroll}
            fixedEndDate={
              selectedSession?.endsAt
                ? formatDateTimeDisplay(selectedSession.endsAt, MOMENT_FORMATS.MED_DATE_ONLY_DISPLAY)
                : undefined
            }
            isOpen={creationMode || hasUpdatedAutoEnroll || Boolean(isSelectionDisabled)}
            onChangeAutoEnroll={onChangeAutoEnroll}
            isAvailabilityEnded={isAvailabilityEnded}
            productId={productId}
          />
        </Grid>
        <Divider color="light" css={{ width: '100%' }} />
      </React.Fragment>
    </Grid>
  );
};

export default PrivateSessionEditDrawerStep1;
